/**
 * src/lib/voice/voiceLogger.js — Voice pipeline observability (thin wrapper)
 *
 * Per PAUL Plan 92-01 (milestone v22.0 Observability Spine), this module is
 * now a THIN WRAPPER over the canonical lib/obs/log.mjs logEvent() helper.
 * Existing callers (~60 callsites) continue to work unchanged — the row
 * shape produced in app_error_logs is byte-identical to pre-migration when
 * called with the legacy signature, and gains canonical observability
 * columns (trace_id, source, module, step, status, duration_ms, etc.) when
 * callers pass them in `meta`.
 *
 * Canonical step order (one name per stage — reconciled 2026-06-06):
 *   mic_start -> stt_input -> stt -> packet_built -> agent_send -> agent_stream
 *   -> tts_invocation -> tts_fetch -> tts_playback -> turn_latency -> turn_complete
 * NOTE: the playback step is 'tts_playback' (Phase 0). The earlier doc said
 * 'tts_play'; that name is retired — emits and this doc now agree.
 * Each step logs: step name, duration_ms, success/error, metadata, trace_id.
 * 'turn_latency' is the single end-to-end roll-up row per turn carrying
 * { total_ms, stt_ms, agent_ms, tts_ms } keyed by the turn trace_id.
 *
 * Query all voice events:
 *   SELECT * FROM app_error_logs WHERE view_name = 'VoiceConversation' ORDER BY created_at DESC;
 *
 * Query failures only:
 *   SELECT * FROM app_error_logs WHERE view_name = 'VoiceConversation' AND error_type = 'voice_error' ORDER BY created_at DESC;
 *
 * Query by trace (NEW — when callers thread trace_id through meta):
 *   SELECT * FROM app_error_logs WHERE trace_id = '<uuid>' ORDER BY created_at;
 */

import { supabase } from '../supabase';
import { logEvent, setSupabaseClient, newTraceId } from '../../../lib/obs/log.mjs';

// Inject the browser supabase singleton into the spine helper at module
// load — logEvent will use it for inserts when called from client code.
setSupabaseClient(supabase);

const VIEW_NAME = 'VoiceConversation';

/**
 * Log a voice pipeline event.
 * @param {string} step - Pipeline step (e.g. 'mic_start', 'whisper.ok', 'tts_queue')
 * @param {'ok'|'error'|'start'|'skip'} status - Step outcome
 * @param {string} message - Human-readable description
 * @param {object} [meta] - Additional metadata. Optional canonical fields:
 *   - trace_id: uuid for end-to-end correlation
 *   - user_id: auth uid
 *   - session_id: browser session id
 *   - scope_id / scope_type: e.g. voice_record id
 *   - duration_ms: timing
 *   - error_code: e.g. NotAllowedError
 *   - source: defaults to 'client'
 *   - page: defaults to window.location.pathname
 */
// TICKET-1079: callers almost never pass user_id, so every client voice_event
// row was anonymous ("did Agustina's recording fail?" was unanswerable). Fall
// back to the signed-in user from the CACHED session — local, no network call.
async function resolveUserId(explicit) {
  if (explicit != null) return explicit;
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

export function logVoiceEvent(step, status, message, meta = {}) {
  const isError = status === 'error';
  const timestamp = new Date().toISOString();
  // Spread meta into both the canonical payload and the legacy metadata so
  // existing dashboards (filter by metadata->>'reason' etc.) and new dashboards
  // (filter by trace_id) both work.
  const restMeta = {};
  for (const [k, v] of Object.entries(meta)) {
    if (k === 'trace_id' || k === 'user_id' || k === 'session_id' || k === 'scope_id'
        || k === 'scope_type' || k === 'duration_ms' || k === 'error_code'
        || k === 'source' || k === 'page' || k === 'view_name' || k === 'error_message') continue;
    restMeta[k] = v;
  }

  return resolveUserId(meta.user_id).then((userId) => logEvent({
    // Canonical columns
    trace_id: meta.trace_id ?? null,
    user_id: userId,
    session_id: meta.session_id ?? null,
    source: meta.source ?? 'client',
    page: meta.page ?? (typeof window !== 'undefined' ? window.location.pathname : null),
    scope_type: meta.scope_type ?? null,
    scope_id: meta.scope_id ?? null,
    module: 'voice',
    step,
    status,
    duration_ms: meta.duration_ms ?? null,
    error_code: meta.error_code ?? null,
    error_message: isError ? (meta.error_message ?? message) : null,

    // Legacy-shape compatibility — the row in app_error_logs continues to
    // populate view_name, error_type, message, and metadata exactly as
    // pre-migration callers expect.
    view_name: meta.view_name ?? VIEW_NAME,
    error_type: isError ? 'voice_error' : 'voice_event',
    message: `[${step}] ${message}`,
    payload: {
      step,
      status,
      timestamp,
      ...restMeta,
    },
  }, { mode: 'lenient' })).catch(() => {});
}

/**
 * Create a step timer for measuring duration.
 * Usage:
 *   const timer = startTimer('stt', { trace_id });
 *   // ... do work ...
 *   timer.ok('Transcribed 42 chars', { text_length: 42 });
 *   // or
 *   timer.fail('Whisper timeout', { timeout_ms: 30000 });
 */
export function startTimer(step, baseMeta = {}) {
  const start = Date.now();
  return {
    ok(message, meta = {}) {
      logVoiceEvent(step, 'ok', message, { ...baseMeta, ...meta, duration_ms: Date.now() - start });
    },
    fail(message, meta = {}) {
      logVoiceEvent(step, 'error', message, { ...baseMeta, ...meta, duration_ms: Date.now() - start });
    },
  };
}

// Re-export so callers can spawn a trace_id without importing lib/obs/log.mjs
// from src/. (Some bundlers track this for chunking; avoiding the deep path
// in component code keeps imports tidy.)
export { newTraceId };
